import { useState } from 'react';
import { Container, Row, Col, Form, Button, Card } from 'react-bootstrap';
import { 
  Building2, 
  Globe, 
  Mail, 
  Phone,
  MapPin,
  Send,
  CheckCircle
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { PageBanner } from '../components/layout/PageBanner';

const Contact = () => {
  const { t, language } = useLanguage();
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // UI Only - Show success message
    setSubmitted(true);
    // Reset form after delay
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', email: '', subject: '', message: '' });
    }, 3000);
  };

  const contactIcons = { Building2, MapPin, Globe, Mail, Phone };
  const contactInfo = [
    {
      iconName: 'Building2',
      label: language === 'hi' ? 'कंपनी का नाम' : 'Company Name',
      value: t('contact.companyName')
    },
    {
      iconName: 'MapPin',
      label: 'CIN',
      value: 'U62099UP2025PTC223917'
    },
    {
      iconName: 'Globe',
      label: t('contact.website'),
      value: 'www.igttech.com',
      link: 'https://www.igttech.com'
    },
    {
      iconName: 'Mail',
      label: t('contact.email'),
      value: 'info@igttech.com',
      link: 'mailto:info@igttech.com'
    },
    {
      iconName: 'Phone',
      label: t('contact.phone'),
      value: '+91-9837741802'
    }
  ];

  return (
    <main id="main-content">
      <PageBanner 
        title={t('contact.pageTitle')} 
        breadcrumb={t('contact.breadcrumb')} 
      />

      {/* Introduction */}
      <section className="gov-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10}>
              <p style={{ 
                fontSize: '1.125rem', 
                color: 'hsl(var(--muted-foreground))',
                textAlign: 'center',
                lineHeight: 1.7
              }}>
                {t('contact.intro')}
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Contact Content */}
      <section className="gov-section gov-section-alt" style={{ paddingTop: 0 }}>
        <Container>
          <Row className="g-5">
            {/* Company Information */}
            <Col lg={5}>
              <h2 style={{ 
                fontSize: '1.25rem', 
                fontWeight: '600', 
                marginBottom: '1.5rem',
                color: 'hsl(var(--foreground))'
              }}>
                {t('contact.companyInfo')}
              </h2>

              <Card className="gov-card">
                <Card.Body className="p-4">
                  {contactInfo.map((item, index) => {
                    const IconComponent = contactIcons[item.iconName];
                    return (
                      <div 
                        key={index}
                        className="d-flex align-items-start gap-3"
                        style={{
                          paddingBottom: index < contactInfo.length - 1 ? '1.25rem' : 0,
                          marginBottom: index < contactInfo.length - 1 ? '1.25rem' : 0,
                          borderBottom: index < contactInfo.length - 1 ? '1px solid hsl(var(--border))' : 'none'
                        }}
                      >
                        <div 
                          style={{
                            width: '40px',
                            height: '40px',
                            background: 'hsl(var(--primary) / 0.1)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}
                        >
                          <IconComponent size={18} style={{ color: 'hsl(var(--primary))' }} />
                        </div>
                        <div>
                          <div style={{ 
                            fontSize: '0.75rem', 
                            color: 'hsl(var(--muted-foreground))',
                            marginBottom: '0.25rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            {item.label}
                          </div>
                          {item.link ? (
                            <a 
                              href={item.link}
                              target={item.link.startsWith('http') ? '_blank' : undefined}
                              rel={item.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                              style={{ 
                                fontWeight: '500',
                                color: 'hsl(var(--primary))',
                                textDecoration: 'none'
                              }}
                            >
                              {item.value}
                            </a>
                          ) : (
                            <div style={{ fontWeight: '500' }}>{item.value}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </Card.Body>
              </Card>

              {/* Quick Note */}
              <div 
                style={{
                  marginTop: '1.5rem',
                  background: 'var(--gradient-gov-banner)',
                  color: 'white',
                  borderRadius: 'var(--radius-lg)',
                  padding: '1.5rem'
                }}
              >
                <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.75rem' }}>
                  {language === 'hi' ? 'कार्यालय समय' : 'Office Hours'}
                </h4>
                <p style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: 0 }}>
                  {language === 'hi' 
                    ? 'सोमवार - शुक्रवार: सुबह 10:00 - शाम 6:00' 
                    : 'Monday - Friday: 10:00 AM - 6:00 PM'}
                </p>
              </div>
            </Col>

            {/* Contact Form */}
            <Col lg={7}>
              <h2 style={{ 
                fontSize: '1.25rem', 
                fontWeight: '600', 
                marginBottom: '1.5rem',
                color: 'hsl(var(--foreground))'
              }}>
                {t('contact.formTitle')}
              </h2>

              <Card className="contact-form">
                <Card.Body className="p-0">
                  {submitted ? (
                    <div className="text-center py-5">
                      <div 
                        style={{
                          width: '64px',
                          height: '64px',
                          background: 'hsl(var(--success) / 0.1)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 1rem'
                        }}
                      >
                        <CheckCircle size={32} style={{ color: 'hsl(var(--success))' }} />
                      </div>
                      <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                        {language === 'hi' ? 'धन्यवाद!' : 'Thank You!'}
                      </h3>
                      <p style={{ color: 'hsl(var(--muted-foreground))' }}>
                        {language === 'hi' 
                          ? 'आपका संदेश सफलतापूर्वक भेजा गया। हम जल्द ही संपर्क करेंगे।' 
                          : 'Your message has been sent successfully. We will contact you soon.'}
                      </p>
                    </div>
                  ) : (
                    <Form onSubmit={handleSubmit}>
                      <Row className="g-3">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                              {t('contact.name')} <span style={{ color: 'hsl(var(--destructive))' }}>*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              required
                              placeholder={language === 'hi' ? 'आपका नाम' : 'Your name'}
                            />
                          </Form.Group>
                        </Col>
                        
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                              {t('contact.emailLabel')} <span style={{ color: 'hsl(var(--destructive))' }}>*</span>
                            </Form.Label>
                            <Form.Control
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              required
                              placeholder={language === 'hi' ? 'आपका ईमेल' : 'Your email'}
                            />
                          </Form.Group>
                        </Col>

                        <Col xs={12}>
                          <Form.Group>
                            <Form.Label style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                              {t('contact.subject')} <span style={{ color: 'hsl(var(--destructive))' }}>*</span>
                            </Form.Label>
                            <Form.Select
                              name="subject"
                              value={formData.subject}
                              onChange={handleChange}
                              required
                            >
                              <option value="">{language === 'hi' ? 'विषय चुनें' : 'Select subject'}</option>
                              <option value="general">{t('contact.subjects.general')}</option>
                              <option value="services">{t('contact.subjects.services')}</option>
                              <option value="training">{t('contact.subjects.training')}</option>
                              <option value="partnership">{t('contact.subjects.partnership')}</option>
                              <option value="careers">{t('contact.subjects.careers')}</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>

                        <Col xs={12}>
                          <Form.Group>
                            <Form.Label style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                              {t('contact.message')} <span style={{ color: 'hsl(var(--destructive))' }}>*</span>
                            </Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={5}
                              name="message"
                              value={formData.message}
                              onChange={handleChange}
                              required
                              placeholder={language === 'hi' ? 'आपका संदेश' : 'Your message'}
                            />
                          </Form.Group>
                        </Col>

                        <Col xs={12}>
                          <Button 
                            type="submit" 
                            className="btn-gov-primary d-inline-flex align-items-center gap-2"
                          >
                            <Send size={18} />
                            {t('contact.submit')}
                          </Button>
                        </Col>
                      </Row>
                    </Form>
                  )}
                </Card.Body>
              </Card>

              {/* Note */}
              <p style={{ 
                fontSize: '0.8125rem', 
                color: 'hsl(var(--muted-foreground))',
                marginTop: '1rem'
              }}>
                {language === 'hi' 
                  ? '* यह फॉर्म केवल UI उद्देश्यों के लिए है। वास्तविक सबमिशन बैकएंड एकीकरण के बाद सक्षम किया जाएगा।' 
                  : '* This form is for UI purposes only. Actual submission will be enabled after backend integration.'}
              </p>
            </Col>
          </Row>
        </Container>
      </section>
    </main>
  );
};

export default Contact;
